import { GraphQLResult } from "@aws-amplify/api";
import { DocumentNode } from "graphql";

import { AnyObject, IdentityContext } from "@examspark/types";

export type GraphQLAuth =
  | {
      type: "API_KEY";
      apiKey: string;
    }
  | {
      type: "AWS_IAM";
      credentials?: {
        accessKeyId: string;
        secretAccessKey: string;
        sessionToken?: string;
      };
    }
  | { type: "ACCESS_TOKEN"; token: string };

export type GraphQLAuthType = GraphQLAuth["type"];

export type GraphQLParams = {
  query: string | DocumentNode;
  variables?: Record<string, any>;
  auth?: GraphQLAuthType | GraphQLAuth;
  overrideIdentityContext?: Partial<IdentityContext>;
};

export type GraphQLClient = {
  query: <T>(params: GraphQLParams) => Promise<GraphQLResult<T>>;
};

export class GraphQLAPI {
  private static clientInternal: GraphQLClient | undefined;

  // For backwards compatibility
  /**
   * @deprecated Use GraphQLAPI.query().
   */
  static get client() {
    return {
      query: <T extends object>(
        params: GraphQLParams & {
          // For backwards compatibility
          /**
           * @deprecated Use the auth property
           */
          authMode?: "API_KEY";
        },
      ) => {
        if (params.auth == null) {
          params.auth = params.authMode;
        }
        return this.query<T>(params);
      },
    };
  }

  static config(client: GraphQLClient) {
    this.clientInternal = client;
  }

  static query = async <T extends object = AnyObject>(
    params: GraphQLParams,
  ) => {
    if (this.clientInternal == null) {
      throw new Error("GraphQLAPI.configure has not been called");
    }

    const query = gqlToString(params.query);
    const result = await this.clientInternal.query<T>({ ...params, query });

    if (result.errors) {
      console.warn(`GraphQL query returned errors`, {
        params,
        errors: result.errors,
      });
    }

    return result;
  };
}

export const gqlToString = (gql: DocumentNode | string) => {
  if (typeof gql === "string") return gql;

  if (gql.loc == null) {
    // This should be impossible, just here to make TS happy
    throw new Error(`Could not serialize GQL node ${gql}`);
  }

  return gql.loc.source.body;
};
